<template>
    <BaseCard padding="lg" class="flex flex-col gap-6 max-w-150 mt-6">
        <h2 class="text-ink title-body">{{ t("feedback.request.question.heading", { name: userDetails.first_name ?? "" }) }}</h2>
        <div class="title-body text-ink">{{ question }}</div>
        <div class="flex flex-wrap justify-between items-center gap-2.5">
            <BaseButton theme="tertiary" class="inline-flex items-center gap-2" @click="handleShowPreview()">
                {{ t("feedback.request.question.previewInvitation") }}
                <i class="bi bi-arrows-angle-expand" aria-hidden="true"></i>
            </BaseButton>
            <div class="flex gap-2">
                <BaseButton theme="primary" :disabled="buttonClicked" @click="copyLink()">{{ buttonText }}</BaseButton>
                <BaseButton theme="tertiary" :disabled="urlButtonClicked" @click="copyUrl()">{{ urlButtonText }}</BaseButton>
            </div>
        </div>
    </BaseCard>
    <BaseDialog v-model:open="showPreview" :title="t('feedback.request.question.previewDialogTitle')" width="wide" mobile-height="expanded" show-close>
        <FeedbackResponseLandingPage preview class="md:p-4" :recipient_users_name="userDetails.first_name ?? ''" />
    </BaseDialog>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { GlobalProperties, UserDetails } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import { useTimedShow } from "~vue/composables/useTimedShow";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, type Ref, ref } from "vue";
import { useI18n } from "vue-i18n";

import FeedbackResponseLandingPage from "./FeedbackResponseLandingPage.vue";

const props = defineProps<{ question: string; messageId: number }>();

const { t } = useI18n();

const { emitter } = inject<GlobalProperties>("globalProperties")!;
const userDetails = inject<Ref<UserDetails>>("user_details")!;
const coachingSessionUuid = inject<string>("coachingSessionUuid");

const { show: showCopied, isShowing: buttonClicked } = useTimedShow({ duration: 1500 });
const buttonText = computed(() => (buttonClicked.value ? t("feedback.request.copied") : t("feedback.request.copyInvite")));

const { show: showUrlCopied, isShowing: urlButtonClicked } = useTimedShow({ duration: 1500 });
const urlButtonText = computed(() => (urlButtonClicked.value ? t("feedback.request.copied") : t("feedback.request.copyUrl")));

const showPreview = ref(false);
const handleShowPreview = () => {
    if (!showPreview.value === true) {
        logUserInteraction("feedback_landing_page_previewed", {}, coachingSessionUuid);
    }
    showPreview.value = !showPreview.value;
};

const {
    execute: createFeedbackRequestQuestion,
    data: feedbackQuestion,
    onFetchResponse: onCreateFeedbackQuestion,
} = useFetch("/api/accounts/feedback-questions", { immediate: false })
    .post(
        computed(() => ({
            question: props.question,
            chat_message: props.messageId,
        })),
    )
    .json();

const { execute: generateFeedbackLink, data: feedbackReview } = useFetch("/api/accounts/feedback-url", { immediate: false })
    .post(computed(() => ({ question_id: feedbackQuestion.value?.id })))
    .json();

onCreateFeedbackQuestion(async () => {
    logUserInteraction(
        "feedback_request_question_created",
        {
            question: props.question,
            question_id: feedbackQuestion.value?.hashid,
        },
        coachingSessionUuid,
    );
});

function showError(message: string) {
    emitter.emit("error_notification", message);
}

const feedbackReviewLink = computed(() => feedbackReview.value?.url);

const copyLink = async () => {
    if (!feedbackReviewLink.value) {
        try {
            await createFeedbackRequestQuestion();
            await generateFeedbackLink();
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : null;
            logUserInteraction("feedback_request_link_copy_error", { error: errorMessage ?? "Could not copy the feedback invitation." }, coachingSessionUuid);
            showError(errorMessage ?? t("feedback.request.question.copyInviteError"));
            return;
        }
    }
    logUserInteraction("feedback_request_link_copied", { feedback_request_id: feedbackQuestion.value?.hashid }, coachingSessionUuid);

    if (feedbackReviewLink.value) {
        const message = t("feedback.request.inviteMessage", { question: props.question, url: feedbackReviewLink.value });
        navigator.clipboard.writeText(message).then(() => {
            showCopied();
        });
    } else {
        logUserInteraction(
            "feedback_request_link_copy_error",
            { error: "No feedback review link available", feedback_request_id: feedbackQuestion.value?.hashid },
            coachingSessionUuid,
        );
        showError(t("feedback.request.question.linkGenerationError"));
    }
};

const copyUrl = async () => {
    if (!feedbackReviewLink.value) {
        try {
            await createFeedbackRequestQuestion();
            await generateFeedbackLink();
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : null;
            logUserInteraction("feedback_request_url_copy_error", { error: errorMessage ?? "Could not copy the feedback URL." }, coachingSessionUuid);
            showError(errorMessage ?? t("feedback.request.question.copyUrlError"));
            return;
        }
    }
    logUserInteraction("feedback_request_url_copied", { feedback_request_id: feedbackQuestion.value?.hashid }, coachingSessionUuid);

    if (feedbackReviewLink.value) {
        try {
            await navigator.clipboard.writeText(feedbackReviewLink.value);
            showUrlCopied();
        } catch {
            showError(t("feedback.request.question.clipboardError", { url: feedbackReviewLink.value }));
        }
    } else {
        logUserInteraction(
            "feedback_request_url_copy_error",
            { error: "No feedback review link available", feedback_request_id: feedbackQuestion.value?.hashid },
            coachingSessionUuid,
        );
        showError(t("feedback.request.question.linkGenerationError"));
    }
};
</script>
